/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isBetween from 'dayjs/plugin/isBetween';
import { useDrag } from '@use-gesture/react';
import useControlled from '@one-thd/sui-atomic-components/dist/private/hooks/useControlled';
import { ArrowForward } from '@one-thd/sui-atomic-components/dist/private/icons/ArrowForward';
import { ArrowBack } from '@one-thd/sui-atomic-components/dist/private/icons/ArrowBack';
import { SortDesc } from '@one-thd/sui-atomic-components/dist/private/icons/SortDesc';
import { IconButton } from '@one-thd/sui-atomic-components';
import { ViewChangerButton } from '../private/components/calendar/ViewChangerButton';
import { CalendarWeekHeader } from '../private/components/calendar/CalendarWeekHeader';
import { CalendarCell } from './CalendarCell';
import { MonthPicker } from './MonthPicker';
import { YearPicker } from './YearPicker';
import {
  addDays, isSameDate, normalizeWeeksShown, getWeekRangeDates, formatWeekRangeTitle
} from './CalendarUtils';

dayjs.extend(isToday);
dayjs.extend(isBetween);

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'
];

const WEEK_SIZE = 7;
const CALENDAR_LIMIT = 2;

const SWIPE_THRESHOLD = 50;
const VELOCITY_THRESHOLD = 0.25;

const showMonthAndYear = (month, year) => {
  return month > 11 ? `${MONTHS[month - 12]} ${year + 1}` : `${MONTHS[month]} ${year}`;
};

const formatDay = (string) => {
  return parseInt(string, 10);
};

const DateCalendar = React.forwardRef((props, ref) => {

  const {
    autoFocus,
    calendars = 1,
    getDataSlot = () => {},
    defaultValue: defaultValueProp,
    unavailableDates: excludeArray,
    layout = 'month',
    maxDate,
    minDate,
    multiple,
    onBlur,
    onChange,
    onFocus,
    readOnly,
    value: valueProp,
    weeksShown: weeksShownProp = 1
  } = props;

  const weeksShown = normalizeWeeksShown(weeksShownProp);
  const isWeekLayout = layout === 'week';

  let calendarIterations;
  if (isWeekLayout) {
    calendarIterations = 1;
  } else {
    calendarIterations = Math.max(1, (Math.min(CALENDAR_LIMIT, calendars)));
  }
  const numberOfCalendars = Array(calendarIterations).fill().map((el, index) => index);

  const [selectedDates, setSelectedDates] = useControlled({
    controlled: valueProp,
    defaultValue: defaultValueProp
  });

  const getInitialDay = () => {
    if (selectedDates) {
      return Array.isArray(selectedDates)
        ? dayjs(selectedDates[selectedDates.length - 1])
        : dayjs(selectedDates);
    }
    return dayjs();
  };

  const [selectedDay, setSelectedDay] = useState(getInitialDay());
  const [selectedMonth, setSelectedMonth] = useState(dayjs(selectedDates).month() || dayjs().month());
  const [selectedYear, setSelectedYear] = useState(dayjs(selectedDates).year() || dayjs().year());
  const [selectedPage, setSelectedPage] = useState(0);

  const [currentView, setCurrentView] = useState('calendar');
  const [internalFocusedDay, setInternalFocusedDay] = useState(selectedDates || dayjs());
  const [internalHasFocus, setInternalHasFocus] = useControlled({
    controlled: autoFocus
  });

  let monthInclusionRange = selectedMonth + (calendars - 1);

  useEffect(() => {
    if (valueProp) {
      if (Array.isArray(valueProp)) {
        const valuePropMonth = dayjs(selectedDates.at(-1)).month();
        setInternalFocusedDay(dayjs(valueProp[valueProp.length - 1]));
        setSelectedDay(dayjs(valueProp[valueProp.length - 1]));
        if (valuePropMonth < selectedMonth || valuePropMonth > monthInclusionRange) {
          setSelectedMonth(dayjs(valueProp[0]).month());
        }
        setSelectedYear(dayjs(valueProp[0]).year());
      } else {
        const parsedDate = dayjs(selectedDates);
        const valuePropMonth = parsedDate.month();
        setInternalFocusedDay(valueProp);
        setSelectedDay(parsedDate);
        if (valuePropMonth < selectedMonth || valuePropMonth > monthInclusionRange) {
          setSelectedMonth(parsedDate.month());
        }
        setSelectedYear(parsedDate.year());
      }
    }
  }, [valueProp]);

  const checkIfExclude = (dateValue) => {
    let isExcluded = false;
    let isBeforeMin = false;
    let isAfterMax = false;
    if (excludeArray) {
      isExcluded = excludeArray.some((el) => dateValue.isSame(dayjs(el), 'days'));
    }
    if (minDate) {
      isBeforeMin = dateValue.isBefore(dayjs(minDate));
    }
    if (maxDate) {
      isAfterMax = dateValue.isAfter(dayjs(maxDate));
    }
    const isDisabled = isExcluded || isBeforeMin || isAfterMax;
    return isDisabled;
  };

  const checkIfSelected = (dateValue) => {
    if (selectedDates) {
      if (Array.isArray(selectedDates)) {
        return (selectedDates.some((el) => (
          dateValue.isSame(dayjs(el), 'days')
        )));
      }
      return dateValue.isSame(dayjs(selectedDates), 'days');
    }
    return false;
  };

  const mapDateValue = (dateValue, month) => {
    return {
      date: dateValue.toDate(),
      day: dateValue.date(),
      now: dateValue.isToday(),
      month,
      data: getDataSlot(dateValue.format()),
      disabled: checkIfExclude(dateValue),
      selected: checkIfSelected(dateValue),
      isFirst: dateValue.date() === dateValue.startOf('month').date(),
      isLast: dateValue.date() === dateValue.endOf('month').date()
    };
  };

  const generateDates = (year, month) => {
    const target = new Date(year, month);
    const parsedDate = dayjs(target);
    const daysInMonth = parsedDate.daysInMonth();

    const monthEndOffset = 6 - parsedDate.endOf('month').day();
    const startDateOffset = parsedDate.startOf('month').day();

    const prevMonth = parsedDate.subtract(1, 'months');
    const nextMonth = parsedDate.add(1, 'months');

    const prevMonthLastDay = prevMonth.endOf('month').date();
    const currentMonthDays = Array.from(
      { length: daysInMonth }, (val, i) => mapDateValue(parsedDate.date(i + 1), 'current')
    );
    const prevMonthDays = Array.from(
      { length: startDateOffset }, (val, index) => mapDateValue(prevMonth.date(prevMonthLastDay - startDateOffset + index), 'prev')
    );
    const nextMonthDays = Array.from({ length: monthEndOffset }, (val, index) => mapDateValue(nextMonth.date(index + 1), 'next'));

    const mergedDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
    return mergedDays;
  };

  const generateWeekDates = (date, weeks) => {
    const { start } = getWeekRangeDates(date, weeks);
    const totalDays = weeks * 7;
    const dates = [];

    for (let i = 0; i < totalDays; i += 1) {
      const currentDate = start.add(i, 'days');
      dates.push(mapDateValue(currentDate, 'current'));
    }

    return dates;
  };

  const splitIntoWeeks = (dates) => {
    const weeks = [];
    for (let i = 0; i < dates.length; i += WEEK_SIZE) {
      const chunk = dates.slice(i, i + WEEK_SIZE);
      weeks.push(chunk);
    }
    return weeks;
  };

  const calendarDates = useMemo(() => {
    if (isWeekLayout) {
      const weekDates = generateWeekDates(selectedDay, weeksShown);
      return [splitIntoWeeks(weekDates)];
    }
    const monthDates = numberOfCalendars.map(
      (el) => splitIntoWeeks(generateDates(selectedYear, selectedMonth + el))
    );
    return monthDates;
  }, [selectedDates, selectedMonth, selectedYear, isWeekLayout, selectedDay, weeksShown]);

  const selectSingleDate = (event, value) => {
    const finalValue = isSameDate(value, selectedDates) ? undefined : value;
    setSelectedDates(finalValue);
    if (onChange) {
      const formattedValue = finalValue && dayjs(finalValue).format();
      onChange(event, formattedValue);
    }
  };

  const selectMultipleDates = (event, value) => {
    let finalValue = [];
    if (Array.isArray(selectedDates)) {
      if (selectedDates.some((el) => isSameDate(value, el))) {
        const filteredArray = selectedDates.filter((el) => !(isSameDate(value, el)));
        finalValue = [...filteredArray];
      } else {
        finalValue = [...selectedDates, value];
      }
    } else {
      finalValue = [value];
    }
    setSelectedDates(finalValue);
    if (onChange) {
      const finalParsedArray = finalValue.map((el) => (dayjs(el).format()));
      onChange(event, finalParsedArray);
    }
  };

  const handleOnChange = (event, value) => {
    setInternalFocusedDay(value);
    if (multiple) {
      selectMultipleDates(event, value);
    } else {
      selectSingleDate(event, value);
    }
  };

  const moveFocus = (focusedDate, value, granularity = 'days') => {
    const newFocusDate = addDays(focusedDate, value, granularity);
    const newFocusedMonth = newFocusDate.month();
    const newFocusedYear = newFocusDate.year();

    if (isWeekLayout) {
      const { start, end } = getWeekRangeDates(selectedDay, weeksShown);
      const isInRange = newFocusDate.isBetween(start, end, 'day', '[]');
      if (!isInRange) {
        if (newFocusDate.isAfter(end) && (value === 1 || value === 7) && granularity === 'days') {
          setSelectedDay(start.add(7, 'days'));
        } else {
          setSelectedDay(newFocusDate);
        }
      }
    }

    if (newFocusedMonth < selectedMonth || newFocusedMonth > monthInclusionRange) {
      setSelectedMonth(newFocusedMonth);
    }
    if (newFocusedYear !== selectedYear) {
      setSelectedYear(newFocusedYear);
    }
    if (Array.isArray(excludeArray) && excludeArray.some((el) => isSameDate(el, newFocusDate))) {
      return moveFocus(newFocusDate, value);
    }
    if (maxDate && newFocusDate.isAfter(maxDate)) {
      const parsedMaxDate = dayjs(maxDate);
      setSelectedMonth(parsedMaxDate.month());
      setSelectedYear(parsedMaxDate.year());
      setInternalFocusedDay(dayjs(maxDate));
      return;
    }
    if (minDate && newFocusDate.isBefore(minDate)) {
      const parsedMinDate = dayjs(minDate);
      setSelectedMonth(parsedMinDate.month());
      setSelectedYear(parsedMinDate.year());
      setInternalFocusedDay(dayjs(minDate));
      return;
    }
    setInternalFocusedDay(newFocusDate);
  };

  const handleNextButton = (event) => {
    if (isWeekLayout) {
      moveFocus(selectedDay, weeksShown * 7);
      return;
    }
    switch (currentView) {
      case 'month':
        setSelectedYear((prevState) => (prevState + 1));
        break;
      case 'year':
        setSelectedPage((prevState) => prevState + 1);
        break;
      default: {
        if (selectedMonth < 11) {
          setInternalFocusedDay(dayjs(new Date(selectedYear, selectedMonth + 1)));
          setSelectedMonth((prevState) => (prevState + 1));
        } else {
          setInternalFocusedDay(dayjs(new Date(selectedYear + 1, 0)));
          setSelectedMonth(0);
          setSelectedYear((prevState) => (prevState + 1));
        }
        break;
      }
    }
  };

  const handleBackButton = (event) => {
    if (isWeekLayout) {
      moveFocus(selectedDay, weeksShown * -7);
      return;
    }
    switch (currentView) {
      case 'month':
        setSelectedYear((prevState) => (prevState - 1));
        break;
      case 'year':
        setSelectedPage((prevState) => prevState - 1);
        break;
      default: {
        if (selectedMonth > 0) {
          setInternalFocusedDay(dayjs(new Date(selectedYear, selectedMonth - 1)));
          setSelectedMonth((prevState) => (prevState - 1));
        } else {
          setInternalFocusedDay(dayjs(new Date(selectedYear - 1, 11)));
          setSelectedMonth(11);
          setSelectedYear((prevState) => (prevState - 1));
        }
        break;
      }
    }
  };

  const dragBind = useDrag((params = {}) => {
    const { event, movement: [mx], last, velocity: [vx] } = params;

    if (event.pointerType === 'pen') return;

    if (mx > SWIPE_THRESHOLD) {
      if (last && vx > VELOCITY_THRESHOLD) {
        handleBackButton(event);
      }
    }

    if (mx < -SWIPE_THRESHOLD) {
      if (last && vx > VELOCITY_THRESHOLD) {
        handleNextButton(event);
      }
    }

  }, {
    axis: 'x',
    filterTaps: true,
    pointer: { touch: true }
  });

  const handleMonthChange = (event, value) => {
    if (!Number.isNaN(value)) {
      setInternalFocusedDay(dayjs(new Date(selectedYear, value)));
      setSelectedMonth(value);
      setCurrentView('calendar');
    }
  };

  const handleYearChange = (event, value) => {
    if (!Number.isNaN(value)) {
      setInternalFocusedDay(dayjs(new Date(value, selectedMonth)));
      setSelectedYear(value);
      setCurrentView('calendar');
      setSelectedPage(0);
    }
  };

  const focusDay = (event, value) => {
    setInternalHasFocus(true);

    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event, value) => {
    if (internalHasFocus) {
      setInternalHasFocus(false);
    }

    if (onBlur) {
      onBlur(event);
    }
  };

  const keyLog = {};
  const handleKeyDown = (event) => {
    const nextItemKey = 'ArrowRight';
    const previousItemKey = 'ArrowLeft';
    const nextRowKey = 'ArrowDown';
    const previousRowKey = 'ArrowUp';
    const firstDayOfWeekKey = 'Home';
    const lastDayOfWeekKey = 'End';
    const nextMonthKey = 'PageDown';
    const previousMonthKey = 'PageUp';
    const modifierKey = 'Shift';

    if (event.type === 'keydown') {
      keyLog[event.key] = true;
    } else if (event.type === 'keyup') {
      delete keyLog[event.key];
    }

    switch (event.key) {
      case nextItemKey:
        event.preventDefault();
        moveFocus(internalFocusedDay, 1);
        break;
      case previousItemKey:
        event.preventDefault();
        moveFocus(internalFocusedDay, -1);
        break;
      case nextRowKey:
        event.preventDefault();
        moveFocus(internalFocusedDay, 7);
        break;
      case previousRowKey:
        event.preventDefault();
        moveFocus(internalFocusedDay, -7);
        break;
      case firstDayOfWeekKey:
        event.preventDefault();
        moveFocus(internalFocusedDay.day(0), 0);
        break;
      case lastDayOfWeekKey:
        event.preventDefault();
        moveFocus(internalFocusedDay.day(6), 0);
        break;
      case nextMonthKey:
        event.preventDefault();
        if (keyLog[modifierKey]) {
          moveFocus(internalFocusedDay, 1, 'year');
          break;
        }
        moveFocus(internalFocusedDay, 1, 'month');
        break;
      case previousMonthKey:
        event.preventDefault();
        if (keyLog[modifierKey]) {
          moveFocus(internalFocusedDay, -1, 'year');
          break;
        }
        moveFocus(internalFocusedDay, -1, 'month');
        break;
      default:
        break;
    }
  };

  const onInvalidFocus = (focusedDate) => {
    moveFocus(focusedDate, 1);
  };

  let yearPageRangeString = `${selectedYear + (selectedPage * 12)} - ${selectedYear + (selectedPage * 12) + 11}`;
  let nextButtonLabel = currentView === 'year' ? 'next range of 12 years' : 'next month';
  let previousButtonLabel = currentView === 'year' ? 'previous range of 12 years' : 'previous month';

  if (isWeekLayout) {
    const { start, end } = getWeekRangeDates(selectedDay, weeksShown);
    const nextRange = getWeekRangeDates(dayjs(selectedDay).add(weeksShown, 'weeks'), weeksShown);
    const prevRange = getWeekRangeDates(dayjs(selectedDay).subtract(weeksShown, 'weeks'), weeksShown);

    const currentTitle = formatWeekRangeTitle(start, end);
    const nextTitle = formatWeekRangeTitle(nextRange.start, nextRange.end);
    const prevTitle = formatWeekRangeTitle(prevRange.start, prevRange.end);

    nextButtonLabel = `Go to next date range, ${nextTitle.aria}`;
    previousButtonLabel = `Go to previous date range, ${prevTitle.aria}`;
  } else if (currentView === 'calendar') {
    const nextMonthDate = selectedMonth < 11
      ? dayjs(new Date(selectedYear, selectedMonth + 1))
      : dayjs(new Date(selectedYear + 1, 0));
    const prevMonthDate = selectedMonth > 0
      ? dayjs(new Date(selectedYear, selectedMonth - 1))
      : dayjs(new Date(selectedYear - 1, 11));

    nextButtonLabel = `Go to next month, ${nextMonthDate.format('MMMM')}`;
    previousButtonLabel = `Go to previous month, ${prevMonthDate.format('MMMM')}`;
  }

  const handleViewChange = () => {
    if (isWeekLayout) return;
    switch (currentView) {
      case 'month':
        setCurrentView('year');
        break;
      case 'year':
        setCurrentView('calendar');
        break;
      default:
        setCurrentView('month');
    }
  };

  const calendarClasses = classNames('sui-text-center sui-min-w-[280px] sui-max-w-[336px] sui-w-full sui-mb-2');
  const navigationClasses = classNames('sui-w-full sui-flex sui-justify-between sui-max-h-[32px] sui-mb-2 sui-min-h-[32px]');

  const getTitleContent = (calendarIndex) => {
    if (isWeekLayout) {
      const { start, end } = getWeekRangeDates(selectedDay, weeksShown);
      const formatted = formatWeekRangeTitle(start, end);
      return {
        visual: formatted.visual,
        aria: formatted.aria,
        showCaret: false
      };
    }

    if (currentView === 'calendar' || calendarIndex !== 0) {
      const monthYear = `${showMonthAndYear(selectedMonth + calendarIndex, selectedYear)}`;
      const aria = currentView === 'calendar'
        ? `${monthYear}, Select another month`
        : monthYear;
      return {
        visual: monthYear,
        aria,
        showCaret: true
      };
    }

    if (currentView === 'month' && calendarIndex === 0) {
      return {
        visual: selectedYear.toString(),
        aria: selectedYear.toString(),
        showCaret: true
      };
    }

    if (currentView === 'year' && calendarIndex === 0) {
      return {
        visual: yearPageRangeString,
        aria: yearPageRangeString,
        showCaret: false
      };
    }

    return {
      visual: '',
      aria: '',
      showCaret: false
    };
  };

  return (
    <div className="sui-flex sui-justify-center sui-gap-6" ref={ref} {...dragBind()}>
      {calendarDates.map((calendarWeeks, calendarIndex) => {
        const titleContent = getTitleContent(calendarIndex);
        return (
          <div className={calendarClasses} key={`Calendar ${calendarIndex}`}>
            <div className={navigationClasses}>
              <ViewChangerButton
                ariaLabel={titleContent.aria}
                disabled={isWeekLayout}
                onClick={handleViewChange}
              >
                <>
                  {titleContent.visual}
                  {titleContent.showCaret && <SortDesc size="small" />}
                </>
              </ViewChangerButton>
              {(calendars === 1 || (calendars > 1 && calendarIndex !== 0)) && (
                <div>
                  <IconButton
                    aria-label={previousButtonLabel}
                    icon={ArrowBack}
                    onClick={handleBackButton}
                  />
                  <IconButton
                    aria-label={nextButtonLabel}
                    icon={ArrowForward}
                    onClick={handleNextButton}
                  />
                </div>
              )}
            </div>
            {(currentView === 'calendar' || calendarIndex !== 0) && (
              <table className="sui-w-full">
                <CalendarWeekHeader />
                <tbody
                  onKeyDown={handleKeyDown}
                >
                  {calendarWeeks.map((row, index) => (
                    <tr className="sui-grid sui-grid-cols-7" key={index}>
                      {row.map((el) => (
                        <td
                          key={el.date}
                          className="sui-max-h-[48px] sui-min-h-[40px] sui-max-w-[48px] sui-min-w-[40px] sui-aspect-square sui-p-0"
                        >
                          <CalendarCell
                            current={el.now}
                            disabled={el.month !== 'current' || el.disabled}
                            focusable={internalFocusedDay}
                            insideCurrentMonth={el.month === 'current'}
                            isViewFocused={internalHasFocus}
                            selected={el.selected}
                            data={el.data}
                            range={el.range}
                            readOnly={readOnly}
                            value={el.date}
                            key={el.date}
                            onClick={handleOnChange}
                            onFocus={focusDay}
                            onBlur={handleBlur}
                            onInvalidFocus={onInvalidFocus}
                          >
                            {el.month === 'current' ? formatDay(el.day) : ''}
                          </CalendarCell>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(currentView === 'month' && calendarIndex === 0) && (
              <MonthPicker
                autoFocus={internalHasFocus}
                month={selectedMonth}
                onChange={handleMonthChange}
              />
            )}
            {(currentView === 'year' && calendarIndex === 0) && (
              <YearPicker
                autoFocus={internalHasFocus}
                year={selectedYear}
                page={selectedPage}
                onChange={handleYearChange}
              />
            )}
          </div>
        );
      })}
    </div>
  );
});

DateCalendar.displayName = 'DateCalendar';

DateCalendar.propTypes = {
  /**
   * If `true`, the component will be focused during initial mount.
   * @default false
   */
  autoFocus: PropTypes.bool,
  /**
   * Number of calendars to render.
   * @default 1
   */
  calendars: PropTypes.oneOf([1, 2]),
  /**
   * The default value selected in the Calendar during initial mouint. Use when the component is not controlled.
   */
  defaultValue: PropTypes.string,
  /**
   * Adds extra content to each cell's data slot. Function receives an ISO formatted date string
   * and the returned content is passed to the data slot.
   * __Note:__ Expensive logic can impact performance.
   */
  getDataSlot: PropTypes.func,
  /**
   * Layout mode for the calendar. `month` displays a full month view, `week` displays continuous weeks.
   * @default 'month'
   */
  layout: PropTypes.oneOf(['month', 'week']),
  /**
   * Values in the array will be compared against the ones in the calendar. If a match is found, they will be disabled.
   */
  unavailableDates: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.instanceOf(Date))
  ]),
  /**
   * Maximal selectable date.
   */
  maxDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  /**
   * Minimal selectable date.
   */
  minDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  /**
   * If `true`, will make it possible to select more than one separate date at a time.
   * To control a multiple calendar, it will require an array as the value.
   */
  multiple: PropTypes.bool,
  /**
   * @ignore
   */
  onBlur: PropTypes.func,
  /**
   * Event fired when the user changes the selected date.
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   */
  onFocus: PropTypes.func,
  /**
   * If `true`, will make the component read only.
   */
  readOnly: PropTypes.bool,
  /**
   * The value of the selected date. If using multiple selection, an array will be used instead.
   */
  value: PropTypes.string,
  /**
   * Number of weeks to show when layout is 'week'.
   * @default 1
   */
  weeksShown: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
};

export { DateCalendar };
